import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  ReactNode,
} from 'react';
import {
  Song,
  EditorState,
  EditorAction,
  createDefaultEditorState,
  createEmptySong,
  SongSection,
  SongLine,
  ChordPlacement,
  VocalAssignment,
} from '../types/music';
import songService from '../services/songService';
import authService from '../services/authService';
import importService from '../services/importService';
import {
  // useMemoryManager,
  useDebouncedCallback,
  // useMemoryMonitoring,
} from '../utils/memoryManagement';

// Context type
interface MusicEditorContextType {
  state: EditorState;
  dispatch: React.Dispatch<EditorAction>;
  // Helper functions
  createNewSong: (userId?: string) => Promise<void>;
  saveSong: () => Promise<void>;
  loadSong: (songId: string) => Promise<void>;
  addSection: (name: string, position?: number) => void;
  addLine: (sectionId: string, lyrics: string, position?: number) => void;
  addChord: (lineId: string, chord: string, position: number) => void;
  // Import functions
  importSong: (file: File) => Promise<void>;
  acceptImport: (draftSong?: Song) => void;
  cancelImport: () => void;
  retryImport: () => Promise<void>;
  canUndo: boolean;
  canRedo: boolean;
}

// Create context
const MusicEditorContext = createContext<MusicEditorContextType | undefined>(
  undefined,
);

const clearAssignmentsForLine = (
  assignments: VocalAssignment[] | undefined,
  lineId: string,
) => (assignments || []).filter(assignment => assignment.lineId !== lineId);

const clearAssignmentsForSection = (
  assignments: VocalAssignment[] | undefined,
  sectionId: string,
) => {
  return (assignments || []).filter(
    assignment => assignment.sectionId !== sectionId,
  );
};

// Reducer function
const editorReducer = (
  state: EditorState,
  action: EditorAction,
): EditorState => {
  switch (action.type) {
    case 'SET_MODE':
      return { ...state, mode: action.payload };

    case 'LOAD_SONG':
      return {
        ...state,
        currentSong: action.payload,
        isDirty: false,
        selectedSectionId: action.payload.sections[0]?.id || null,
        selectedLineId: action.payload.sections[0]?.lines[0]?.id || null,
      };

    case 'UPDATE_SONG_TITLE':
      if (!state.currentSong) return state;
      return {
        ...state,
        currentSong: {
          ...state.currentSong,
          title: action.payload,
          modifiedAt: new Date(),
        },
        isDirty: true,
      };

    case 'UPDATE_SONG_KEY':
      if (!state.currentSong) return state;
      return {
        ...state,
        currentSong: {
          ...state.currentSong,
          key: action.payload,
          modifiedAt: new Date(),
        },
        isDirty: true,
      };

    case 'UPDATE_SONG_CAPO':
      if (!state.currentSong) return state;
      return {
        ...state,
        currentSong: {
          ...state.currentSong,
          capo: action.payload,
          modifiedAt: new Date(),
        },
        isDirty: true,
      };

    case 'UPDATE_SONG_TEMPO':
      if (!state.currentSong) return state;
      return {
        ...state,
        currentSong: {
          ...state.currentSong,
          tempo: action.payload,
          modifiedAt: new Date(),
        },
        isDirty: true,
      };

    case 'ADD_SECTION':
      if (!state.currentSong) return state;
      const newSection: SongSection = {
        id: `section_${Date.now()}`,
        name: action.payload.name,
        lines: [
          {
            id: `line_${Date.now()}`,
            lyrics: '',
            chords: [],
          },
        ],
        collapsed: false,
        order: action.payload.position ?? state.currentSong.sections.length,
      };

      const sectionsWithNew = [...state.currentSong.sections];
      if (action.payload.position !== undefined) {
        sectionsWithNew.splice(action.payload.position, 0, newSection);
        // Update order for sections after insertion point
        sectionsWithNew.forEach((section, index) => {
          section.order = index;
        });
      } else {
        sectionsWithNew.push(newSection);
      }

      return {
        ...state,
        currentSong: {
          ...state.currentSong,
          sections: sectionsWithNew,
          modifiedAt: new Date(),
        },
        selectedSectionId: newSection.id,
        selectedLineId: newSection.lines[0].id,
        isDirty: true,
      };

    case 'UPDATE_SECTION':
      if (!state.currentSong) return state;
      return {
        ...state,
        currentSong: {
          ...state.currentSong,
          sections: state.currentSong.sections.map(section =>
            section.id === action.payload.sectionId
              ? { ...section, ...action.payload.updates }
              : section,
          ),
          modifiedAt: new Date(),
        },
        isDirty: true,
      };

    case 'DELETE_SECTION':
      if (!state.currentSong) return state;
      const filteredSections = state.currentSong.sections.filter(
        s => s.id !== action.payload,
      );
      return {
        ...state,
        currentSong: {
          ...state.currentSong,
          sections: filteredSections,
          vocalAssignments: clearAssignmentsForSection(
            state.currentSong.vocalAssignments,
            action.payload,
          ),
          modifiedAt: new Date(),
        },
        selectedSectionId: filteredSections[0]?.id || null,
        selectedLineId: filteredSections[0]?.lines[0]?.id || null,
        isDirty: true,
      };

    case 'ADD_LINE':
      if (!state.currentSong) return state;
      const newLine: SongLine = {
        id: `line_${Date.now()}`,
        lyrics: action.payload.lyrics,
        chords: [],
      };

      return {
        ...state,
        currentSong: {
          ...state.currentSong,
          sections: state.currentSong.sections.map(section =>
            section.id === action.payload.sectionId
              ? {
                  ...section,
                  lines:
                    action.payload.position !== undefined
                      ? [
                          ...section.lines.slice(0, action.payload.position),
                          newLine,
                          ...section.lines.slice(action.payload.position),
                        ]
                      : [...section.lines, newLine],
                }
              : section,
          ),
          modifiedAt: new Date(),
        },
        selectedLineId: newLine.id,
        isDirty: true,
      };

    case 'UPDATE_LINE_LYRICS':
      if (!state.currentSong) return state;
      return {
        ...state,
        currentSong: {
          ...state.currentSong,
          sections: state.currentSong.sections.map(section => ({
            ...section,
            lines: section.lines.map(line =>
              line.id === action.payload.lineId
                ? { ...line, lyrics: action.payload.lyrics }
                : line,
            ),
          })),
          vocalAssignments: clearAssignmentsForLine(
            state.currentSong.vocalAssignments,
            action.payload.lineId,
          ),
          modifiedAt: new Date(),
        },
        isDirty: true,
      };

    case 'REPLACE_VOCAL_ASSIGNMENTS':
      if (!state.currentSong) return state;
      return {
        ...state,
        currentSong: {
          ...state.currentSong,
          vocalAssignments: action.payload,
          modifiedAt: new Date(),
        },
        isDirty: true,
      };

    case 'DELETE_LINE':
      if (!state.currentSong) return state;
      return {
        ...state,
        currentSong: {
          ...state.currentSong,
          sections: state.currentSong.sections.map(section => ({
            ...section,
            lines: section.lines.filter(line => line.id !== action.payload),
          })),
          vocalAssignments: clearAssignmentsForLine(
            state.currentSong.vocalAssignments,
            action.payload,
          ),
          modifiedAt: new Date(),
        },
        isDirty: true,
      };

    case 'ADD_CHORD':
      if (!state.currentSong) return state;
      const newChord: ChordPlacement = {
        id: `chord_${Date.now()}`,
        chord: action.payload.chord,
        position: action.payload.position,
      };

      return {
        ...state,
        currentSong: {
          ...state.currentSong,
          sections: state.currentSong.sections.map(section => ({
            ...section,
            lines: section.lines.map(line =>
              line.id === action.payload.lineId
                ? {
                    ...line,
                    chords: [...line.chords, newChord].sort(
                      (a, b) => a.position - b.position,
                    ),
                  }
                : line,
            ),
          })),
          modifiedAt: new Date(),
        },
        isDirty: true,
      };

    case 'UPDATE_CHORD':
      if (!state.currentSong) return state;
      return {
        ...state,
        currentSong: {
          ...state.currentSong,
          sections: state.currentSong.sections.map(section => ({
            ...section,
            lines: section.lines.map(line => ({
              ...line,
              chords: line.chords
                .map(chord =>
                  chord.id === action.payload.chordId
                    ? {
                        ...chord,
                        ...(action.payload.chord && {
                          chord: action.payload.chord,
                        }),
                        ...(action.payload.position !== undefined && {
                          position: action.payload.position,
                        }),
                      }
                    : chord,
                )
                .sort((a, b) => a.position - b.position),
            })),
          })),
          modifiedAt: new Date(),
        },
        isDirty: true,
      };

    case 'DELETE_CHORD':
      if (!state.currentSong) return state;
      return {
        ...state,
        currentSong: {
          ...state.currentSong,
          sections: state.currentSong.sections.map(section => ({
            ...section,
            lines: section.lines.map(line => ({
              ...line,
              chords: line.chords.filter(chord => chord.id !== action.payload),
            })),
          })),
          modifiedAt: new Date(),
        },
        isDirty: true,
      };

    case 'SELECT_SECTION':
      return { ...state, selectedSectionId: action.payload };

    case 'SELECT_LINE':
      return { ...state, selectedLineId: action.payload };

    case 'SELECT_CHORD':
      return { ...state, selectedChordId: action.payload };

    case 'SET_EDITING':
      return { ...state, isEditing: action.payload };

    case 'SET_DIRTY':
      return { ...state, isDirty: action.payload };

    case 'SAVE_SONG':
      return {
        ...state,
        isDirty: false,
        lastSaved: new Date(),
      };

    case 'UNDO':
      if (state.historyIndex <= 0 || state.history.length === 0) return state;
      const previousSong = state.history[state.historyIndex - 1];
      return {
        ...state,
        currentSong: previousSong,
        historyIndex: state.historyIndex - 1,
        isDirty: true,
      };

    case 'REDO':
      if (state.historyIndex >= state.history.length - 1) return state;
      const nextSong = state.history[state.historyIndex + 1];
      return {
        ...state,
        currentSong: nextSong,
        historyIndex: state.historyIndex + 1,
        isDirty: true,
      };

    case 'TOGGLE_CHORD_PICKER':
      return {
        ...state,
        showChordPicker: action.payload ?? !state.showChordPicker,
      };

    case 'TOGGLE_SECTION_MANAGER':
      return {
        ...state,
        showSectionManager: action.payload ?? !state.showSectionManager,
      };

    case 'START_CHORD_DRAG':
      return {
        ...state,
        dragState: {
          isDragging: true,
          draggedChordId: action.payload.chordId,
          draggedChordLineId: action.payload.lineId,
          dragPreviewPosition: action.payload.position,
          targetLineId: action.payload.lineId,
          targetPosition: action.payload.originalPosition,
          originalPosition: action.payload.originalPosition,
        },
      };

    case 'UPDATE_CHORD_DRAG':
      return {
        ...state,
        dragState: {
          ...state.dragState,
          dragPreviewPosition: action.payload.position,
          targetLineId:
            action.payload.targetLineId ?? state.dragState.targetLineId,
          targetPosition:
            action.payload.targetPosition ?? state.dragState.targetPosition,
        },
      };

    case 'END_CHORD_DRAG':
      // Determine values from action payload or current drag state
      const newLineId =
        action.payload.newLineId || state.dragState.targetLineId;
      const newPosition =
        action.payload.newPosition !== undefined
          ? action.payload.newPosition
          : state.dragState.targetPosition;
      const isValidDrop =
        action.payload.success &&
        state.dragState.draggedChordId &&
        newLineId &&
        newPosition !== null &&
        newPosition !== undefined;

      if (isValidDrop) {
        // Move chord to new position
        const draggedChordId = state.dragState.draggedChordId;

        let updatedSong = state.currentSong;
        let draggedChord: ChordPlacement | null = null;

        // First, find and remove the dragged chord
        if (updatedSong) {
          updatedSong = {
            ...updatedSong,
            sections: updatedSong.sections.map(section => ({
              ...section,
              lines: section.lines.map(line => ({
                ...line,
                chords: line.chords.filter(chord => {
                  if (chord.id === draggedChordId) {
                    draggedChord = { ...chord, position: newPosition };
                    return false;
                  }
                  return true;
                }),
              })),
            })),
          };

          // Then, add the chord to its new position
          if (draggedChord) {
            updatedSong = {
              ...updatedSong,
              sections: updatedSong.sections.map(section => ({
                ...section,
                lines: section.lines.map(line =>
                  line.id === newLineId
                    ? {
                        ...line,
                        chords: [
                          ...line.chords,
                          draggedChord as ChordPlacement,
                        ].sort((a, b) => a.position - b.position),
                      }
                    : line,
                ),
              })),
              modifiedAt: new Date(),
            };
          }
        }

        return {
          ...state,
          currentSong: updatedSong,
          isDirty: true,
          dragState: {
            isDragging: false,
            draggedChordId: null,
            draggedChordLineId: null,
            dragPreviewPosition: null,
            targetLineId: null,
            targetPosition: null,
            originalPosition: null,
          },
        };
      }

      return {
        ...state,
        dragState: {
          isDragging: false,
          draggedChordId: null,
          draggedChordLineId: null,
          dragPreviewPosition: null,
          targetLineId: null,
          targetPosition: null,
          originalPosition: null,
        },
      };

    case 'CANCEL_CHORD_DRAG':
      return {
        ...state,
        dragState: {
          isDragging: false,
          draggedChordId: null,
          draggedChordLineId: null,
          dragPreviewPosition: null,
          targetLineId: null,
          targetPosition: null,
          originalPosition: null,
        },
      };

    // Import actions
    case 'TOGGLE_IMPORT_MODAL':
      return {
        ...state,
        importState: {
          ...state.importState,
          showImportModal: action.payload ?? !state.importState.showImportModal,
        },
      };

    case 'START_IMPORT':
      return {
        ...state,
        importState: {
          ...state.importState,
          isImporting: true,
          currentFile: action.payload.file,
          progress: 0,
          importResult: null,
          validation: null,
          metadata: null,
          diagnostics: null,
          error: null,
          isRetryable: false,
          retryDelay: 0,
          retryAttempt: 0,
        },
      };

    case 'UPDATE_IMPORT_PROGRESS':
      return {
        ...state,
        importState: {
          ...state.importState,
          progress: action.payload.progress,
        },
      };

    case 'IMPORT_SUCCESS':
      return {
        ...state,
        importState: {
          ...state.importState,
          isImporting: false,
          importResult: action.payload.song,
          validation: action.payload.validation,
          metadata: action.payload.metadata,
          diagnostics: action.payload.diagnostics || null,
          error: null,
          progress: 100,
        },
      };

    case 'IMPORT_ERROR':
      return {
        ...state,
        importState: {
          ...state.importState,
          isImporting: false,
          error: action.payload.error,
          isRetryable: action.payload.isRetryable,
          retryDelay: action.payload.retryDelay,
          diagnostics: null,
          progress: 0,
        },
      };

    case 'RESET_IMPORT':
      return {
        ...state,
        importState: {
          isImporting: false,
          showImportModal: false,
          progress: 0,
          currentFile: null,
          importResult: null,
          validation: null,
          metadata: null,
          diagnostics: null,
          error: null,
          isRetryable: false,
          retryDelay: 0,
          retryAttempt: 0,
          maxRetries: 3,
        },
      };

    case 'RETRY_IMPORT':
      return {
        ...state,
        importState: {
          ...state.importState,
          isImporting: true,
          error: null,
          progress: 0,
          retryAttempt: state.importState.retryAttempt + 1,
        },
      };

    case 'ACCEPT_IMPORT':
      if (!state.importState.importResult) return state;
      const acceptedSong = action.payload || state.importState.importResult;
      const hasReviewChanges = Boolean(
        action.payload && action.payload !== state.importState.importResult,
      );
      return {
        ...state,
        currentSong: acceptedSong,
        isDirty: hasReviewChanges,
        lastSaved: hasReviewChanges ? null : new Date(),
        selectedSectionId: acceptedSong.sections[0]?.id || null,
        selectedLineId: acceptedSong.sections[0]?.lines[0]?.id || null,
        importState: {
          isImporting: false,
          showImportModal: false,
          progress: 0,
          currentFile: null,
          importResult: null,
          validation: null,
          metadata: null,
          diagnostics: null,
          error: null,
          isRetryable: false,
          retryDelay: 0,
          retryAttempt: 0,
          maxRetries: 3,
        },
      };

    case 'CANCEL_IMPORT':
      return {
        ...state,
        importState: {
          isImporting: false,
          showImportModal: false,
          progress: 0,
          currentFile: null,
          importResult: null,
          validation: null,
          metadata: null,
          diagnostics: null,
          error: null,
          isRetryable: false,
          retryDelay: 0,
          retryAttempt: 0,
          maxRetries: 3,
        },
      };

    default:
      return state;
  }
};

// Auto-save constants
const AUTO_SAVE_DELAY = 2000; // 2 seconds
const STORAGE_KEY = 'music_editor_songs';

// Provider component
interface MusicEditorProviderProps {
  children: ReactNode;
}

export const MusicEditorProvider: React.FC<MusicEditorProviderProps> = ({
  children,
}) => {
  // Memory management (temporarily disabled)
  // const _memoryManager = useMemoryManager();
  // useMemoryMonitoring('MusicEditorProvider', 30000);

  const [state, dispatch] = useReducer(
    editorReducer,
    createDefaultEditorState(),
  );

  // Debounced auto-save to reduce unnecessary saves
  const debouncedAutoSave = useDebouncedCallback(async (song: Song) => {
    try {
      // Save to local storage (replacing AsyncStorage)
      const existingSongs = localStorage.getItem(STORAGE_KEY);
      const songs: Song[] = existingSongs ? JSON.parse(existingSongs) : [];

      const songIndex = songs.findIndex(s => s._id === song._id);
      if (songIndex >= 0) {
        songs[songIndex] = song;
      } else {
        songs.push(song);
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(songs));
    } catch (error) {
      console.error('Failed to save song:', error);
    }
  }, AUTO_SAVE_DELAY);

  // Auto-save effect with memory management
  useEffect(() => {
    if (state.isDirty && state.currentSong) {
      debouncedAutoSave(state.currentSong);
    }
  }, [state.isDirty, state.currentSong, debouncedAutoSave]);

  // Helper functions
  const createNewSong = async (userId?: string) => {
    try {
      // If no userId provided, get from auth service
      if (!userId) {
        const currentUser = await authService.getCurrentUser();
        userId = currentUser?._id;
      }

      const newSong = createEmptySong(userId || 'current_user');
      dispatch({ type: 'LOAD_SONG', payload: newSong });
    } catch (error) {
      console.error('Failed to get current user for new song:', error);
      // Fallback to a default user ID
      const newSong = createEmptySong('current_user');
      dispatch({ type: 'LOAD_SONG', payload: newSong });
    }
  };

  const saveSong = async () => {
    if (!state.currentSong) return;

    try {
      // First, save to local storage as backup
      await saveToLocalStorage(state.currentSong);

      // Try to save to backend
      const isOnline = await songService.checkConnection();
      if (isOnline) {
        try {
          const savedSong = await songService.saveSong(state.currentSong);
          // Update state with the saved song (which has the server ID)
          dispatch({ type: 'LOAD_SONG', payload: savedSong });
          dispatch({ type: 'SAVE_SONG' });
        } catch (backendError) {
          console.error(
            'Backend save failed, keeping local copy:',
            backendError,
          );
          throw new Error(
            'Saved locally only. The server save failed, so this song will not appear in My Songs until you save it successfully.',
          );
        }
      } else {
        console.log('Offline - saved locally only');
        throw new Error(
          'Saved locally only. The backend is unavailable, so this song will not appear in My Songs yet.',
        );
      }
    } catch (error) {
      console.error('Failed to save song:', error);
      throw error;
    }
  };

  const saveToLocalStorage = async (song: Song) => {
    const existingSongs = localStorage.getItem(STORAGE_KEY);
    const songs: Song[] = existingSongs ? JSON.parse(existingSongs) : [];

    const songIndex = songs.findIndex(s => s._id === song._id);
    if (songIndex >= 0) {
      songs[songIndex] = song;
    } else {
      songs.push(song);
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(songs));
  };

  const loadSong = async (songId: string) => {
    try {
      // First try to load from backend
      const isOnline = await songService.checkConnection();
      if (isOnline && !songId.startsWith('local_')) {
        try {
          const song = await songService.getSong(songId);
          dispatch({ type: 'LOAD_SONG', payload: song });
          // Also save to local storage as backup
          await saveToLocalStorage(song);
          return;
        } catch (backendError) {
          console.error(
            'Backend load failed, trying local storage:',
            backendError,
          );
        }
      }

      // Fallback to local storage
      const existingSongs = localStorage.getItem(STORAGE_KEY);
      const songs: Song[] = existingSongs ? JSON.parse(existingSongs) : [];
      const song = songs.find(s => s._id === songId);

      if (song) {
        dispatch({ type: 'LOAD_SONG', payload: song });
      } else {
        throw new Error('Song not found');
      }
    } catch (error) {
      console.error('Failed to load song:', error);
    }
  };

  const addSection = (name: string, position?: number) => {
    dispatch({ type: 'ADD_SECTION', payload: { name, position } });
  };

  const addLine = (sectionId: string, lyrics: string, position?: number) => {
    dispatch({ type: 'ADD_LINE', payload: { sectionId, lyrics, position } });
  };

  const addChord = (lineId: string, chord: string, position: number) => {
    dispatch({ type: 'ADD_CHORD', payload: { lineId, chord, position } });
  };

  // Import helper functions
  const importSong = async (file: File) => {
    try {
      dispatch({ type: 'START_IMPORT', payload: { file } });

      const result = await importService.importSong(file, {
        onProgress: (loaded, total) => {
          const progress = Math.round((loaded / total) * 100);
          dispatch({ type: 'UPDATE_IMPORT_PROGRESS', payload: { progress } });
        },
      });

      if (
        result.success &&
        result.song &&
        result.validation &&
        result.metadata
      ) {
        dispatch({
          type: 'IMPORT_SUCCESS',
          payload: {
            song: result.song,
            validation: result.validation,
            metadata: result.song.importMetadata || {
              sourceFile: result.metadata.filename,
              mimeType: result.metadata.mimeType,
              fileSize: result.metadata.size,
              processingTime: result.metadata.processingTime,
              confidence: result.metadata.confidence,
            },
            diagnostics: result.metadata
              ? {
                  pdfStrategy: result.metadata.pdfStrategy,
                  pdfImageFallbackUsed: result.metadata.pdfImageFallbackUsed,
                  pdfTextPathUsed: result.metadata.pdfTextPathUsed,
                  aiInputMimeType: result.metadata.aiInputMimeType,
                  pdfDiagnostics: result.metadata.pdfDiagnostics || null,
                }
              : null,
          },
        });
      } else {
        dispatch({
          type: 'IMPORT_ERROR',
          payload: {
            error: result.error || 'Import failed',
            isRetryable: result.retryable || false,
            retryDelay: result.retryDelay || 0,
          },
        });
      }
    } catch (error: any) {
      console.error('Import failed:', error);
      dispatch({
        type: 'IMPORT_ERROR',
        payload: {
          error: error.message || 'Import failed',
          isRetryable: false,
          retryDelay: 0,
        },
      });
    }
  };

  const acceptImport = (draftSong?: Song) => {
    dispatch({ type: 'ACCEPT_IMPORT', payload: draftSong });
  };

  const cancelImport = () => {
    dispatch({ type: 'CANCEL_IMPORT' });
  };

  const retryImport = async () => {
    if (!state.importState.currentFile) return;

    if (state.importState.retryAttempt >= state.importState.maxRetries) {
      dispatch({
        type: 'IMPORT_ERROR',
        payload: {
          error: 'Maximum retry attempts reached',
          isRetryable: false,
          retryDelay: 0,
        },
      });
      return;
    }

    dispatch({ type: 'RETRY_IMPORT' });

    // Wait for retry delay if specified
    if (state.importState.retryDelay > 0) {
      await new Promise(resolve =>
        setTimeout(resolve, state.importState.retryDelay),
      );
    }

    await importSong(state.importState.currentFile);
  };

  const canUndo = state.historyIndex > 0;
  const canRedo = state.historyIndex < state.history.length - 1;

  const contextValue: MusicEditorContextType = {
    state,
    dispatch,
    createNewSong,
    saveSong,
    loadSong,
    addSection,
    addLine,
    addChord,
    importSong,
    acceptImport,
    cancelImport,
    retryImport,
    canUndo,
    canRedo,
  };

  return (
    <MusicEditorContext.Provider value={contextValue}>
      {children}
    </MusicEditorContext.Provider>
  );
};

// Custom hook to use the context
export const useMusicEditor = (): MusicEditorContextType => {
  const context = useContext(MusicEditorContext);
  if (!context) {
    throw new Error('useMusicEditor must be used within a MusicEditorProvider');
  }
  return context;
};
